import {
	AppButton,
	AvatarGroup,
	Checkbox,
	ConfirmDialog,
	CountBadge,
	Divider,
	Input,
	IntegrationLogo,
	ListItem,
	Pagination,
	Surface,
	Table,
	TableBody,
	TableCell,
	TableCellAvatar,
	TableCellText,
	TableHead,
	TableHeader,
	TableRow,
	Toggle,
	Tooltip,
	integrationLabel,
	usePopoverMotion
} from '@clickguard/design-system'
import ArrowUp from '@untitled-ui/icons-react/build/cjs/ArrowUp'
import RefreshCw02 from '@untitled-ui/icons-react/build/cjs/RefreshCw02'
import SearchLg from '@untitled-ui/icons-react/build/cjs/SearchLg'
import {
	ArrowDown,
	ChevronDown,
	ChevronRight,
	ChevronSelectorVertical,
	Edit05,
	FilterLines,
	InfoCircle,
	SearchSm,
	Shield01,
	XClose
} from 'components/icons'
import {
	Fragment,
	KeyboardEvent,
	ReactNode,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from 'react'
import { FadingScrollArea } from 'components/fading-scroll/fading-scroll-area'
import { useTrackPostHogEvent } from 'utils/posthog'
import { PostHogEvent, PostHogPath } from 'utils/posthog-events'
import { RuleEntities, useRuleEntities } from './rule-entities'
import { applyTemplateScope } from './template-adapters'
import { RuleDetailsDrawer } from './rule-details-drawer'
import { MAX_CHART_RULES } from './rules-activity-chart'
import { RuleSheet } from './rule-sheet'
import { SaveAsTemplateDialog } from './save-as-template-dialog'
import { SwapValue, swapDelay } from './swap-value'
import { TemplatePicker } from './template-picker'
import {
	CustomRule,
	RuleActions,
	RuleDraft,
	RulePlatform,
	RuleScopeMode,
	RuleStatus,
	RuleTemplate,
	TemplateScope
} from './types'

interface CustomRulesTableProps {
	rules: CustomRule[]
	/** Rule ids charted below — the leading checkbox column, capped at 5. */
	chartIds: Set<string>
	onChartIdsChange: (next: Set<string>) => void
	/** Header time frame — the row Details rail charts the last N days. */
	days: number
	/** Re-fetches rules and executions. Owned by the page, which holds both. */
	onRefresh: () => void
	isRefreshing: boolean
	/** True only until the rules query first answers. Distinct from `isRefreshing`,
	 *  which is also true on a Refresh and must NOT hide the rules already on screen. */
	isLoading: boolean
	/** Persisted through the page — see its handlers for the service calls. */
	onSaveRule: (draft: RuleDraft, ruleId?: string) => void | Promise<void>
	onToggleRule: (ruleId: string, nextActive: boolean) => void
	onDeleteRule: (ruleId: string) => void
	onDuplicateRule: (ruleId: string) => void
	/** The workspace's template library — the caller's own plus everything shared. */
	templates: RuleTemplate[]
	onSaveAsTemplate: (ruleId: string, name: string, scope: TemplateScope) => void
	onToggleTemplateVisibility: (template: RuleTemplate) => void
	onDeleteTemplate: (template: RuleTemplate) => void
	onSaveTemplate: (templateId: string, draft: RuleDraft) => void | Promise<void>
}

// ── Scope — one resolution feeds the cell text, the marks, the hover and the sort ──

/** One thing a rule reaches, and the platform that thing runs on. */
type ScopeTarget = { name: string; platform?: RulePlatform }

/** A campaign carries no platform of its own — it inherits its account's. */
const accountPlatform = (
	entities: RuleEntities,
	accountId: string
): RulePlatform | undefined =>
	entities.adAccounts.find((account) => account.id === accountId)?.platform

/** `count` is what the rule NAMES (`scope.values`); `targets` is best-effort name
 *  resolution, so reporting its length would under-state a live rule's reach. */
const resolveScope = (
	entities: RuleEntities,
	rule: CustomRule
): { noun: string; count: number; targets: ScopeTarget[] } => {
	const count = rule.scope.values.length
	if (rule.scope.mode === 'accounts')
		return {
			noun: 'ad account',
			count,
			targets: entities.adAccounts
				.filter((account) => rule.scope.values.includes(account.id))
				.map((account) => ({
					name: account.name,
					platform: account.platform
				}))
		}
	if (rule.scope.mode === 'campaigns')
		return {
			noun: 'campaign',
			count,
			targets: entities.campaigns
				.filter((campaign) => rule.scope.values.includes(campaign.id))
				.map((campaign) => ({
					name: campaign.name,
					platform: accountPlatform(entities, campaign.accountId)
				}))
		}
	return { noun: 'website', count: 0, targets: [] }
}

/** Distinct platforms the rule reaches — `rule.platform` names only the first. */
/** The scope discs. The fill needs the `!`: twMerge is `cg-`-prefixed, so a bare
 *  `bg-white` cannot beat `cg-bg-subtle`. */
const SCOPE_DISC = '!bg-white shadow-[0_1px_2px_0_rgba(16,24,40,0.06)]'
/** Microsoft mark only, optical. `size` is a 16/20/24 ladder and Tailwind 3.3.2
 *  has no `size-*`, so it comes in as `h-3.5 w-3.5` with the `!`. */
const MICROSOFT_INSET = '!h-3.5 !w-3.5'
/** The same marks inside the hover list, one step down, beside 12px text. */
const TOOLTIP_MARK = '!h-3 !w-3'
const TOOLTIP_MARK_MICROSOFT = '!h-[10.5px] !w-[10.5px]'
const GOOGLE_OPTICAL_LIFT = '-translate-y-[0.75px]'

const scopePlatforms = (
	entities: RuleEntities,
	rule: CustomRule
): RulePlatform[] => {
	const seen: RulePlatform[] = []
	for (const target of resolveScope(entities, rule).targets)
		if (target.platform && !seen.includes(target.platform))
			seen.push(target.platform)
	return seen
}

/** One target is named, past one it becomes a count — the rule's own count. */
const scopeLabel = (entities: RuleEntities, rule: CustomRule): string => {
	if (rule.scope.mode === 'website') return 'All ad traffic'
	const { noun, count, targets } = resolveScope(entities, rule)
	if (count === 1) return targets[0]?.name ?? `1 ${noun}`
	return `${count} ${noun}s`
}

// ── Sorting — the PA-grid header treatment ───────────────────────────────────
type SortKey = 'enabled' | 'name' | 'scope' | 'triggered' | 'blocked'
type SortDirection = 'asc' | 'desc' | null

const collator = new Intl.Collator(undefined, { sensitivity: 'base' })

/** Widest reach first: the whole website, then ad accounts, then campaigns. */
const SCOPE_ORDER: Record<RuleScopeMode, number> = {
	website: 0,
	accounts: 1,
	campaigns: 2
}

// Sorts by scope: reach, then how many targets a rule names, then alphabetically.
const compareScope = (
	entities: RuleEntities,
	a: CustomRule,
	b: CustomRule
): number => {
	const byMode = SCOPE_ORDER[a.scope.mode] - SCOPE_ORDER[b.scope.mode]
	if (byMode !== 0) return byMode
	const aScope = resolveScope(entities, a)
	const bScope = resolveScope(entities, b)
	if (aScope.count !== bScope.count) return aScope.count - bScope.count
	return collator.compare(
		aScope.targets[0]?.name ?? '',
		bScope.targets[0]?.name ?? ''
	)
}

const compareRules = (
	entities: RuleEntities,
	a: CustomRule,
	b: CustomRule,
	key: SortKey
): number => {
	if (key === 'triggered' || key === 'blocked') return a[key] - b[key]
	// Running rules first on the FIRST click, not the false-before-true default.
	if (key === 'enabled')
		return Number(b.status === 'active') - Number(a.status === 'active')
	if (key === 'scope') return compareScope(entities, a, b)
	return collator.compare(a[key], b[key])
}

const SortHeadCell = ({
	label,
	sortKey,
	sort,
	onSort,
	align,
	className,
	children
}: {
	label: string
	sortKey: SortKey
	sort: { key: SortKey; dir: 'asc' | 'desc' } | null
	onSort: (key: SortKey) => void
	align?: 'right'
	/** Extra classes on the TableHead (e.g. the frozen-column sticky recipe). */
	className?: string
	/** Optional extra content rendered inside the head cell (e.g. edge shadow). */
	children?: ReactNode
}) => {
	const dir: SortDirection = sort?.key === sortKey ? sort.dir : null
	return (
		<TableHead
			className={[align === 'right' ? '!text-right' : '', className ?? '']
				.join(' ')
				.trim()}
		>
			{children}
			{/* The DS focus ring needs no padding — the `-mx-0.5 px-0.5` pair this carried
			    truncated every header, which is sized from its own max-content width. */}
			<button
				type="button"
				onClick={() => onSort(sortKey)}
				className={`group inline-flex max-w-full items-center gap-1 rounded text-left align-middle focus-visible:cg-shadow-focus-sm focus-visible:outline-none ${
					align === 'right' ? 'flex-row-reverse' : ''
				}`}
				aria-label={`Sort by ${label}${
					dir ? (dir === 'asc' ? ', ascending' : ', descending') : ''
				}`}
			>
				<span className="truncate">{label}</span>
				<span className="shrink-0 text-[rgb(var(--cg-text-tertiary))] transition-colors group-hover:text-[rgb(var(--cg-text-secondary))]">
					{dir === 'asc' ? (
						<ArrowUp className="h-3.5 w-3.5" />
					) : dir === 'desc' ? (
						<ArrowDown className="h-3.5 w-3.5" />
					) : (
						<ChevronSelectorVertical className="h-3.5 w-3.5" />
					)}
				</span>
			</button>
		</TableHead>
	)
}

// ── Filter facet menu — the Scheduled Reports recipe ─────────────────────────
// Option rows are ListItem as="div" role="checkbox": the DS Checkbox is a real
// <input> that may not nest inside ListItem's default <button>.

const FILTER_CARD_W = 500
const FILTER_CATEGORIES_W = 224

const FacetMenu = ({
	facets,
	activeId,
	onActivate,
	selections,
	onToggle,
	onClear,
	canClear
}: {
	facets: { id: string; label: string; options: string[] }[]
	activeId: string
	onActivate: (id: string) => void
	selections: Record<string, string[]>
	onToggle: (facetId: string, value: string) => void
	onClear: () => void
	canClear: boolean
}) => {
	const active = facets.find((f) => f.id === activeId) ?? facets[0]
	return (
		<Surface
			bg="primary"
			radius="xl"
			bordered
			shadow="lg"
			className="cg-flex cg-overflow-hidden"
			style={{
				width: FILTER_CARD_W,
				backgroundColor: 'rgb(var(--cg-surface-subtle))'
			}}
		>
			<div
				className="flex shrink-0 flex-col gap-[2px] cg-bg-surface-subtle p-2"
				style={{ width: FILTER_CATEGORIES_W }}
				role="group"
				aria-label="Filter categories"
			>
				{facets.map((facet) => {
					const isActive = facet.id === active.id
					return (
						<ListItem
							key={facet.id}
							as="button"
							label={facet.label}
							badge={<CountBadge count={selections[facet.id].length} />}
							trailingIcon={<ChevronRight />}
							className={isActive ? 'cg-bg-selected' : undefined}
							onClick={() => onActivate(facet.id)}
							// ListItem defaults to role="menuitem"; this popover is not a menu.
							role={undefined}
							aria-current={isActive || undefined}
						/>
					)
				})}
				<div className="mt-auto flex flex-col gap-[2px]">
					<Divider className="my-0.5" />
					<ListItem
						as="button"
						label={
							<span className="font-medium text-[rgb(var(--cg-text-link))]">
								Clear filters
							</span>
						}
						disabled={!canClear}
						onClick={onClear}
						role={undefined}
					/>
				</div>
			</div>

			{/* Only the left arc survives the card's clip — a border-left breaks at the corners. */}
			<div
				className="flex flex-1 flex-col gap-[2px] cg-bg-surface p-2"
				style={{
					// One LESS than the card, which clips at an 11px padding-box corner.
					borderRadius: 11,
					boxShadow:
						'0 0 0 1px rgb(var(--cg-border)), 0 2px 8px -2px rgba(16, 24, 40, 0.14)'
				}}
				role="group"
				aria-label={`${active.label} options`}
			>
				{active.options.map((option) => {
					const checked = selections[active.id].includes(option)
					return (
						<ListItem
							key={option}
							as="div"
							role="checkbox"
							aria-checked={checked}
							tabIndex={0}
							label={option}
							leadingControl={
								<Checkbox
									size="sm"
									checked={checked}
									readOnly
									tabIndex={-1}
									aria-hidden
									className="pointer-events-none"
								/>
							}
							onClick={() => onToggle(active.id, option)}
							onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
								if (e.key === ' ' || e.key === 'Enter') {
									e.preventDefault()
									onToggle(active.id, option)
								}
							}}
						/>
					)
				})}
			</div>
		</Surface>
	)
}

// ── Frozen columns (identity pinned left, actions pinned right) ──────────────
// `!` beats the DS TableCell's `cg-max-w-0` — twMerge can't dedupe cg- classes.
const ACT_W = '!w-[188px] !max-w-[188px]'
// Must equal this column's RENDERED width — the Rule column's sticky offset keys off it.
const ENB_W = '!w-[104px] !min-w-[104px] !max-w-[104px]'
const RULE_LEFT = 'left-[104px]'
// Offset = Enabled + Rule (104 + 310); must equal the rendered width to its left.
const CHK_W = '!w-[84px] !min-w-[84px] !max-w-[84px]'
const CHART_LEFT = 'left-[414px]'

// A sticky cell's opaque bg masks the DS row-hover; replay it via ::before.
// Read the alpha from the token — DS 0.31.1 split it by theme.
const STICKY_HOVER =
	"before:pointer-events-none before:absolute before:inset-0 before:-z-[1] before:bg-[rgb(var(--cg-hover-overlay)/var(--cg-hover-alpha))] before:opacity-0 before:transition-opacity before:duration-150 before:content-[''] group-hover:before:opacity-100"

// border-collapse drops cell box-shadows in Chrome, so the edge is a gradient strip.
const StickyEdgeShadow = ({
	show,
	side = 'left'
}: {
	show: boolean
	side?: 'left' | 'right'
}) => (
	<span
		aria-hidden
		className={`pointer-events-none absolute inset-y-0 z-[1] w-2 transition-opacity duration-200 ${
			side === 'right'
				? 'right-0 translate-x-full bg-gradient-to-r from-[rgba(0,0,0,0.10)] to-[rgba(0,0,0,0)]'
				: 'left-0 -translate-x-full bg-gradient-to-l from-[rgba(0,0,0,0.10)] to-[rgba(0,0,0,0)]'
		} ${show ? 'opacity-100' : 'opacity-0'}`}
	/>
)

// Row disclosure — expands the rule's inline Details panel. Down = collapsed.
const DetailsToggle = ({
	open,
	onToggle,
	ruleName
}: {
	open: boolean
	onToggle: () => void
	ruleName: string
}) => (
	<AppButton
		variant="secondary"
		size="sm"
		label={open ? 'Hide details' : 'See details'}
		rightIcon={
			<ChevronDown
				className={`h-4 w-4 transition-transform duration-150 ${
					open ? 'rotate-180' : ''
				}`}
			/>
		}
		aria-expanded={open}
		aria-label={`${open ? 'Hide' : 'See'} details for ${ruleName}`}
		onClick={onToggle}
	/>
)

/** Held while the FIRST fetch is in flight. Without it the empty state below claims
 *  the website has no rules before the answer is in — it flashed on every load. */
const LoadingState = () => (
	<div className="flex flex-col gap-2 px-6 py-10" aria-busy="true">
		<span className="sr-only">Loading custom rules</span>
		{[0, 1, 2].map((row) => (
			<div
				key={row}
				className="h-10 animate-pulse rounded-lg bg-[rgb(var(--cg-surface-2))]"
			/>
		))}
	</div>
)

const EmptyState = ({
	icon,
	title,
	body,
	action
}: {
	icon: ReactNode
	title: string
	body: string
	action: ReactNode
}) => (
	<div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
		<div className="flex h-12 w-12 items-center justify-center rounded-full border border-[rgb(var(--cg-border))] text-[rgb(var(--cg-text-tertiary))]">
			{icon}
		</div>
		<div className="flex max-w-xs flex-col gap-1">
			<span className="text-base font-semibold text-[rgb(var(--cg-text-primary))]">
				{title}
			</span>
			<span className="text-sm text-[rgb(var(--cg-text-secondary))]">
				{body}
			</span>
		</div>
		{action}
	</div>
)

// Enabled/Disabled, matching the first column's header and its switch.
const STATUS_OPTIONS = ['Enabled', 'Disabled']

/** The click verdicts a rule can set — the sheet's "Set click status" field. */
const CLICK_STATUS_OPTIONS = ['Valid', 'Suspicious', 'Bad']

/** The sheet's action checkboxes mapped to the flags they set. A single derived
 *  "Action" label was winner-takes-all — `blacklist` was tested first. */
const ACTION_FILTERS: {
	label: string
	flag: keyof Pick<
		RuleActions,
		'addLabels' | 'blacklist' | 'tempIpExclusion' | 'audienceExclusion'
	>
}[] = [
	{ label: 'Add labels to click', flag: 'addLabels' },
	{ label: 'Blacklist the source', flag: 'blacklist' },
	{ label: 'Temporarily exclude IP address', flag: 'tempIpExclusion' },
	{ label: 'Add visitor to excluded audience', flag: 'audienceExclusion' }
]

const ACTION_FILTER_LABELS = ACTION_FILTERS.map((action) => action.label)

/** OR within the facet. Blacklisting force-enables both exclusions, so a
 *  blacklisting rule legitimately matches those too. */
const matchesActionFilter = (rule: CustomRule, selected: string[]) =>
	selected.some((label) => {
		const action = ACTION_FILTERS.find((entry) => entry.label === label)
		return action ? rule.actions[action.flag] : false
	})

const statusLabel = (status: RuleStatus) =>
	status === 'active' ? 'Enabled' : 'Disabled'

/** Targets spelled out in the hover list before it gives up and counts. */
const MAX_SPELLED_TARGETS = 6

// Hover readout for the Scope cell: one target a line, each with its own mark.
const scopeTooltip = (
	entities: RuleEntities,
	rule: CustomRule
): { content: string; description: ReactNode } => {
	if (rule.scope.mode === 'website')
		return {
			content: 'Active on this website',
			description: 'Every ad account and campaign'
		}
	const { noun, count, targets } = resolveScope(entities, rule)
	const spelled = targets.slice(0, MAX_SPELLED_TARGETS)
	// Measured against the rule's count, so the lines add up to the header's number.
	const rest = count - spelled.length
	return {
		content: `Active on ${count} ${noun}${count === 1 ? '' : 's'}`,
		description: (
			<span className="flex flex-col gap-1">
				{/* An off-ladder `size` is NO size class at all — the image then renders at its
				    intrinsic 300px. `h-3 w-3` and not `size-3`: Tailwind 3.3.2 has no `size-*`. */}
				{spelled.map((target) => (
					<span key={target.name} className="flex items-center gap-1.5">
						<span className="flex h-3 w-3 shrink-0 items-center">
							{target.platform ? (
								<IntegrationLogo
									provider={target.platform}
									size={16}
									className={
										target.platform === 'microsoft'
											? TOOLTIP_MARK_MICROSOFT
											: TOOLTIP_MARK
									}
									decorative
								/>
							) : null}
						</span>
						{target.name}
					</span>
				))}
				{rest > 0 ? (
					<span className="flex items-center gap-1.5">
						<span className="h-3 w-3 shrink-0" aria-hidden />+{rest} more
					</span>
				) : null}
			</span>
		)
	}
}

export const CustomRulesTable = ({
	rules,
	chartIds,
	onChartIdsChange,
	days,
	onRefresh,
	isRefreshing,
	isLoading,
	onSaveRule,
	onToggleRule,
	onDeleteRule,
	onDuplicateRule,
	templates,
	onSaveAsTemplate,
	onToggleTemplateVisibility,
	onDeleteTemplate,
	onSaveTemplate
}: CustomRulesTableProps) => {
	const entities = useRuleEntities()
	const trackPostHogEvent = useTrackPostHogEvent()
	const [search, setSearch] = useState('')
	const [statusFilter, setStatusFilter] = useState<string[]>([])
	const [clickStatusFilter, setClickStatusFilter] = useState<string[]>([])
	const [actionFilter, setActionFilter] = useState<string[]>([])
	const [filtersOpen, setFiltersOpen] = useState(false)
	const [activeFacet, setActiveFacet] = useState('status')
	const [sort, setSort] = useState<{
		key: SortKey
		dir: 'asc' | 'desc'
	} | null>(null)
	const [page, setPage] = useState(1)
	const [pageSize, setPageSize] = useState(10)
	const [sheetOpen, setSheetOpen] = useState(false)
	const [editingRule, setEditingRule] = useState<CustomRule | null>(null)
	const [sheetDraft, setSheetDraft] = useState<RuleDraft | null>(null)
	const [sheetTemplateName, setSheetTemplateName] = useState<string>()
	// The template being edited (tray "Edit"); null = the sheet is doing rule work.
	const [editingTemplate, setEditingTemplate] = useState<RuleTemplate | null>(
		null
	)
	const [deletingRule, setDeletingRule] = useState<CustomRule | null>(null)
	// The row whose inline Details panel is open (one at a time).
	const [expandedId, setExpandedId] = useState<string | null>(null)
	// The rule pending "Save as template" (null keeps that dialog closed).
	const [savingRule, setSavingRule] = useState<CustomRule | null>(null)
	const refreshing = isRefreshing
	const refresh = () => {
		if (refreshing) return
		onRefresh()
	}

	const activeFilterCount =
		statusFilter.length + clickStatusFilter.length + actionFilter.length

	// A local composition, so it borrows the DS popover motion; `mounted` fades it out.
	const filterMotion = usePopoverMotion(filtersOpen)

	// Close the filter popover on outside click.
	const filterRef = useRef<HTMLDivElement>(null)
	useEffect(() => {
		if (!filtersOpen) return
		const onDown = (e: MouseEvent) => {
			if (filterRef.current && !filterRef.current.contains(e.target as Node))
				setFiltersOpen(false)
		}
		document.addEventListener('mousedown', onDown)
		return () => document.removeEventListener('mousedown', onDown)
	}, [filtersOpen])

	const openCreate = () => {
		setEditingRule(null)
		setSheetDraft(null)
		setSheetTemplateName(undefined)
		setEditingTemplate(null)
		setSheetOpen(true)
	}

	const openEdit = (rule: CustomRule) => {
		setEditingRule(rule)
		setSheetDraft(null)
		setSheetTemplateName(undefined)
		setEditingTemplate(null)
		setSheetOpen(true)
	}

	// Copy semantics: Save mints an independent rule.
	const startFromTemplate = (template: RuleTemplate) => {
		// Fired on TAKE-UP, not on rule creation, which has its own event.
		trackPostHogEvent(
			PostHogEvent.USE_RULE_TEMPLATE,
			PostHogPath.CUSTOM_RULES,
			{
				template_scope: template.scope
			}
		)
		setEditingRule(null)
		// Scope TYPE carries; the selection only survives where the entities do.
		setSheetDraft(applyTemplateScope(template.draft, entities))
		setSheetTemplateName(template.name)
		setEditingTemplate(null)
		setSheetOpen(true)
	}

	// Tray "Edit" — Save writes back into the library rather than minting a rule.
	const openTemplateEdit = (template: RuleTemplate) => {
		setEditingRule(null)
		// Filtered like the take-up path above: the sheet lists THIS website's entities,
		// so carrying another site's ids in behind them is how they got written back into
		// the template under the wrong `asset_id` in the first place.
		setSheetDraft(applyTemplateScope(template.draft, entities))
		setSheetTemplateName(undefined)
		setEditingTemplate(template)
		setSheetOpen(true)
	}

	// Returned for the same reason as `saveRule`: the sheet awaits it before closing.
	const saveTemplate = (draft: RuleDraft) => {
		if (!editingTemplate) return
		return onSaveTemplate(editingTemplate.id, draft)
	}

	// Snapshots the rule's STORED config, the same shape Duplicate re-posts.
	const saveAsTemplate = (
		rule: CustomRule,
		name: string,
		scope: TemplateScope
	) => onSaveAsTemplate(rule.id, name, scope)

	// An unknown id means a create; the rest of the row is server-derived.
	// Returned, not fired and forgotten: the sheet awaits this to decide whether to
	// close, and a dropped promise is a rejection nothing ever sees.
	const saveRule = (nextRule: CustomRule) => {
		const exists = rules.some((rule) => rule.id === nextRule.id)
		return onSaveRule(nextRule, exists ? nextRule.id : undefined)
	}

	const toggleRule = (ruleId: string) => {
		const rule = rules.find((r) => r.id === ruleId)
		if (!rule) return
		// Pausing a rule does not unfire its history, so `lastExecutedAt` is left alone.
		onToggleRule(ruleId, rule.status !== 'active')
	}

	// Chart selection — the leading checkbox column, capped at MAX_CHART_RULES.
	const atChartCap = chartIds.size >= MAX_CHART_RULES
	const toggleChart = (ruleId: string) => {
		const next = new Set(chartIds)
		if (next.has(ruleId)) next.delete(ruleId)
		else if (next.size < MAX_CHART_RULES) next.add(ruleId)
		onChartIdsChange(next)
	}
	const duplicateRule = (rule: CustomRule) => onDuplicateRule(rule.id)

	const deleteRule = (rule: CustomRule) => {
		onDeleteRule(rule.id)
		setDeletingRule(null)
	}

	// Three states: ascending, descending, then back to unsorted.
	const onSort = (key: SortKey) =>
		setSort((s) => {
			if (s?.key !== key) return { key, dir: 'asc' }
			if (s.dir === 'asc') return { key, dir: 'desc' }
			return null
		})

	// Search + facet filters, then sort; pagination slices the result below.
	const processed = useMemo(() => {
		const q = search.trim().toLowerCase()
		const out = rules.filter((r) => {
			if (
				q &&
				!r.name.toLowerCase().includes(q) &&
				!r.description.toLowerCase().includes(q)
			)
				return false
			// OR inside a facet, AND across them.
			if (statusFilter.length && !statusFilter.includes(statusLabel(r.status)))
				return false
			if (
				clickStatusFilter.length &&
				!clickStatusFilter.includes(r.actions.clickStatus)
			)
				return false
			if (actionFilter.length && !matchesActionFilter(r, actionFilter))
				return false
			return true
		})
		if (sort) {
			out.sort((a, b) => {
				const r = compareRules(entities, a, b, sort.key)
				return sort.dir === 'asc' ? r : -r
			})
		}
		return out
	}, [
		rules,
		search,
		statusFilter,
		clickStatusFilter,
		actionFilter,
		sort,
		entities
	])

	const totalPages = Math.max(1, Math.ceil(processed.length / pageSize))
	const currentPage = Math.min(page, totalPages)
	// Memoized: a fresh slice each render made the measure below a nested-update loop.
	const pageRows = useMemo(
		() => processed.slice((currentPage - 1) * pageSize, currentPage * pageSize),
		[processed, currentPage, pageSize]
	)

	// Edge shadow only while content is hidden past the pinned right edge.
	const scrollRef = useRef<HTMLDivElement | null>(null)
	const [edge, setEdge] = useState<{ start: boolean; end: boolean }>({
		start: true,
		end: true
	})
	// Viewport width — the Details panel pins to it so horizontal scroll can't bury it.
	const [viewportW, setViewportW] = useState<number>()
	const measure = useCallback(() => {
		const el = scrollRef.current
		if (!el) return
		const start = el.scrollLeft <= 0
		const end = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1
		setEdge((p) => (p.start === start && p.end === end ? p : { start, end }))
		const w = el.clientWidth
		setViewportW((prev) => (prev === w ? prev : w))
	}, [])
	useLayoutEffect(() => {
		measure()
	}, [measure, pageRows])
	useEffect(() => {
		const el = scrollRef.current
		if (!el || typeof ResizeObserver === 'undefined') return
		const ro = new ResizeObserver(() => measure())
		ro.observe(el)
		return () => ro.disconnect()
	}, [measure])

	const noRules = rules.length === 0
	const noMatches = !noRules && processed.length === 0
	// Left pair casts right once content scrolls under it, the Actions column left.
	const showLeftPinShadow = !edge.start
	const showRightPinShadow = !edge.end

	const facets = [
		{ id: 'status', label: 'Rule status', options: STATUS_OPTIONS },
		{ id: 'clickStatus', label: 'Click status', options: CLICK_STATUS_OPTIONS },
		{ id: 'actions', label: 'Actions', options: ACTION_FILTER_LABELS }
	]
	const facetSelections: Record<string, string[]> = {
		status: statusFilter,
		clickStatus: clickStatusFilter,
		actions: actionFilter
	}
	const toggleFacet = (facetId: string, value: string) => {
		if (facetId === 'status') setStatusFilter((s) => toggleIn(s, value))
		else if (facetId === 'clickStatus')
			setClickStatusFilter((s) => toggleIn(s, value))
		else setActionFilter((s) => toggleIn(s, value))
		setPage(1)
	}
	const resetFilterSelections = () => {
		setStatusFilter([])
		setClickStatusFilter([])
		setActionFilter([])
		setPage(1)
	}
	const clearAll = () => {
		setSearch('')
		resetFilterSelections()
	}

	return (
		<>
			<div className="rounded-xl border border-[rgb(var(--cg-border))] bg-[rgb(var(--cg-surface))] p-3">
				{isLoading ? (
					<LoadingState />
				) : noRules ? (
					<EmptyState
						icon={<Shield01 className="h-6 w-6" />}
						title="No custom rules yet"
						body="Create a rule to take precise control over how suspicious traffic is handled."
						// The picker, not a bare button: the chevron is the ONLY way into the
						// template library (§5), and a website with no rules is exactly where
						// starting from one is worth most.
						action={
							<TemplatePicker
								onCreate={openCreate}
								templates={templates}
								onSelect={startFromTemplate}
								onEditTemplate={openTemplateEdit}
								onToggleVisibility={onToggleTemplateVisibility}
								onDeleteTemplate={onDeleteTemplate}
							/>
						}
					/>
				) : (
					<>
						<div className="flex items-center justify-between gap-3">
							<div className="flex items-center gap-2.5">
								{/* !border matches the toolbar buttons (twMerge can't dedupe cg-). */}
								<div className="w-[180px] [&_span[aria-hidden]]:!pl-1.5">
									<Input
										state="default"
										size="sm"
										className="[&:not(:focus-within)]:!border-[rgb(var(--cg-border))]"
										leadingIcon={<SearchLg className="h-3.5 w-3.5" />}
										style={{ paddingTop: 6, paddingBottom: 6 }}
										placeholder="Search for rules..."
										autoComplete="off"
										value={search}
										onChange={(e) => {
											setSearch(e.target.value)
											setPage(1)
										}}
									/>
								</div>
								<div ref={filterRef} className="relative flex">
									<AppButton
										variant="secondary"
										size="sm"
										label={
											activeFilterCount > 0
												? `Filters · ${activeFilterCount}`
												: 'Filters'
										}
										leftIcon={<FilterLines />}
										onClick={() => setFiltersOpen((o) => !o)}
									/>
									{filterMotion.mounted && (
										<div
											className="absolute left-0 top-[calc(100%+6px)] z-20"
											style={filterMotion.style}
										>
											<FacetMenu
												facets={facets}
												activeId={activeFacet}
												onActivate={setActiveFacet}
												selections={facetSelections}
												onToggle={toggleFacet}
												onClear={resetFilterSelections}
												canClear={activeFilterCount > 0}
											/>
										</div>
									)}
								</div>
							</div>
							<div className="flex items-center gap-2.5">
								<AppButton
									variant="secondary"
									size="sm"
									label="Refresh"
									leftIcon={
										<RefreshCw02
											className={refreshing ? 'animate-spin' : undefined}
										/>
									}
									disabled={refreshing}
									aria-busy={refreshing}
									onClick={refresh}
								/>
								<TemplatePicker
									onCreate={openCreate}
									templates={templates}
									onSelect={startFromTemplate}
									onEditTemplate={openTemplateEdit}
									onToggleVisibility={onToggleTemplateVisibility}
									onDeleteTemplate={onDeleteTemplate}
								/>
							</div>
						</div>
						{noMatches ? (
							<EmptyState
								icon={<SearchSm className="h-6 w-6" />}
								title="No rules match"
								body="Try a different search, or clear your filters to see every custom rule."
								action={
									<AppButton
										variant="secondary"
										size="sm"
										label="Clear filters"
										leftIcon={<XClose />}
										onClick={clearAll}
									/>
								}
							/>
						) : (
							<>
								<FadingScrollArea
									ref={scrollRef}
									onScroll={measure}
									className="mt-3 overflow-x-auto overflow-y-hidden rounded-lg"
								>
									{/* `table-fixed` is load-bearing: under auto layout Refresh restamping
									    "Today, HH:MM" retires the widest date and shifts the middle six columns.
									    The header widths must total the min-w: 104+310+84+240+112+140+140+188. */}
									<Table className="min-w-[1318px] table-fixed [&_th]:!text-[12px] [&_th]:!font-medium [&_td]:!px-4 [&_th]:!px-4">
										<TableHeader className="cg-bg-surface-2">
											<TableRow className="hover:!bg-transparent">
												<SortHeadCell
													label="Enabled"
													sortKey="enabled"
													sort={sort}
													onSort={onSort}
													className={`sticky left-0 z-[2] cg-bg-surface-2 ${ENB_W}`}
												/>
												<SortHeadCell
													label="Rule"
													sortKey="name"
													sort={sort}
													onSort={onSort}
													className={`sticky ${RULE_LEFT} z-[2] cg-bg-surface-2 !w-[310px]`}
												/>
												<TableHead
													className={`sticky ${CHART_LEFT} z-[2] cg-bg-surface-2 ${CHK_W}`}
												>
													<StickyEdgeShadow
														show={showLeftPinShadow}
														side="right"
													/>
													{/* Portaled so the scroller cannot clip it. */}
													<span className="inline-flex items-center gap-1 align-middle">
														<span className="whitespace-nowrap">Chart</span>
														<Tooltip
															content="Show on the chart"
															description={`Tick a rule to plot it on the activity chart below the table, up to ${MAX_CHART_RULES} at a time.`}
															position="top"
															showArrow
															portal
														>
															<span
																tabIndex={0}
																role="img"
																aria-label="Show on the chart"
																className="flex h-3 w-3 shrink-0 cursor-help items-center justify-center rounded-full text-[rgb(var(--cg-icon-secondary)_/_0.7)] transition-colors hover:text-[rgb(var(--cg-icon-secondary))] focus-visible:cg-shadow-focus-sm focus-visible:outline-none [&>svg]:h-3 [&>svg]:w-3"
															>
																<InfoCircle />
															</span>
														</Tooltip>
													</span>
												</TableHead>
												<SortHeadCell
													label="Scope"
													sortKey="scope"
													sort={sort}
													onSort={onSort}
													className="!w-[240px]"
												/>
												<SortHeadCell
													label="Executions"
													sortKey="triggered"
													sort={sort}
													onSort={onSort}
													className="!w-[112px]"
												/>
												<SortHeadCell
													label="Blocked sources"
													sortKey="blocked"
													sort={sort}
													onSort={onSort}
													className="!w-[140px]"
												/>
												<TableHead className="!w-[140px]">
													Last execution
												</TableHead>
												<TableHead
													aria-label="Actions"
													className={`sticky right-0 z-[2] cg-bg-surface-2 ${ACT_W}`}
												>
													<StickyEdgeShadow show={showRightPinShadow} />
												</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{pageRows.map((rule, rowIndex) => {
												const expanded = expandedId === rule.id
												const rowDelay = swapDelay(rowIndex, pageRows.length)
												const scope = scopeTooltip(entities, rule)
												const platforms = scopePlatforms(entities, rule)
												return (
													<Fragment key={rule.id}>
														<TableRow className="group">
															<TableCell
																className={`sticky left-0 z-[1] bg-[rgb(var(--cg-surface))] ${ENB_W} ${STICKY_HOVER}`}
															>
																<span className="flex items-center">
																	<Toggle
																		checked={rule.status === 'active'}
																		onChange={() => toggleRule(rule.id)}
																		size="md"
																		aria-label={`${
																			rule.status === 'active'
																				? 'Disable'
																				: 'Enable'
																		} ${rule.name}`}
																	/>
																</span>
															</TableCell>
															<TableCell
																className={`sticky ${RULE_LEFT} z-[1] !max-w-none bg-[rgb(var(--cg-surface))] ${STICKY_HOVER}`}
															>
																<TableCellText primary={rule.name} />
															</TableCell>
															<TableCell
																className={`sticky ${CHART_LEFT} z-[1] bg-[rgb(var(--cg-surface))] ${CHK_W} ${STICKY_HOVER}`}
															>
																<StickyEdgeShadow
																	show={showLeftPinShadow}
																	side="right"
																/>
																<span className="flex items-center">
																	<Checkbox
																		checked={chartIds.has(rule.id)}
																		disabled={
																			!chartIds.has(rule.id) && atChartCap
																		}
																		onChange={() => toggleChart(rule.id)}
																		size="sm"
																		aria-label={`Chart ${rule.name}`}
																	/>
																</span>
															</TableCell>
															{/* `portal` because the table's overflow would clip the tooltip. A website
													    rule gets no marks, so no avatar slot at all. */}
															<TableCell className="!max-w-none whitespace-normal [&>div]:min-w-0 [&>div]:w-full">
																<Tooltip
																	content={scope.content}
																	description={scope.description}
																	position="top"
																	portal
																>
																	{platforms.length > 0 ? (
																		/* `!` because twMerge is `cg-`-prefixed: `gap-2` cannot beat `cg-gap-3`. */
																		<TableCellAvatar
																			className="w-full !gap-2"
																			avatar={
																				/* `isolate`: AvatarGroup's inline z-index ladder otherwise resolves in the
																		   TABLE's context and paints over the frozen columns at z-[1]. The mark stays
																		   at 16 — below the DS ladder's smallest rung the variant misses entirely. */
																				<AvatarGroup
																					size="xs"
																					className="isolate"
																					avatars={platforms.map(
																						(platform) => ({
																							tone: 'gray',
																							className: SCOPE_DISC,
																							alt: integrationLabel(platform),
																							icon: (
																								<span className="flex h-4 w-4 shrink-0 items-center justify-center">
																									<IntegrationLogo
																										provider={platform}
																										size={16}
																										className={
																											platform === 'microsoft'
																												? MICROSOFT_INSET
																												: platform ===
																												  'google-ads'
																												? GOOGLE_OPTICAL_LIFT
																												: undefined
																										}
																										decorative
																									/>
																								</span>
																							)
																						})
																					)}
																				/>
																			}
																		>
																			<TableCellText
																				className="w-full"
																				primary={scopeLabel(entities, rule)}
																			/>
																		</TableCellAvatar>
																	) : (
																		<TableCellText
																			className="w-full"
																			primary={scopeLabel(entities, rule)}
																		/>
																	)}
																</Tooltip>
															</TableCell>
															<TableCell className="!max-w-none whitespace-nowrap tabular-nums">
																<SwapValue
																	token={rule.triggered}
																	delay={rowDelay}
																>
																	{rule.triggered.toLocaleString()}
																</SwapValue>
															</TableCell>
															<TableCell className="!max-w-none whitespace-nowrap tabular-nums">
																<SwapValue
																	token={rule.blocked}
																	delay={rowDelay}
																>
																	{rule.blocked.toLocaleString()}
																</SwapValue>
															</TableCell>
															<TableCell className="!max-w-none whitespace-nowrap text-[rgb(var(--cg-text-secondary))]">
																{rule.lastExecutedAt}
															</TableCell>
															<TableCell
																className={`sticky right-0 z-[1] bg-[rgb(var(--cg-surface))] ${STICKY_HOVER} ${ACT_W}`}
															>
																<StickyEdgeShadow show={showRightPinShadow} />
																<div className="flex items-center justify-end gap-2">
																	<AppButton
																		variant="secondary"
																		size="sm"
																		label="Edit"
																		leftIcon={<Edit05 className="h-4 w-4" />}
																		hideLabel
																		aria-label={`Edit ${rule.name}`}
																		onClick={() => openEdit(rule)}
																		// The Tailwind build orders `cg-px-0` before `cg-px-s-10`, so without this
																		// aspect-square balloons the button to 38x38.
																		className="!px-0"
																	/>
																	<DetailsToggle
																		open={expanded}
																		ruleName={rule.name}
																		onToggle={() =>
																			setExpandedId((id) =>
																				id === rule.id ? null : rule.id
																			)
																		}
																	/>
																</div>
															</TableCell>
														</TableRow>
														<TableRow className="!border-b-0 hover:!bg-transparent">
															<TableCell
																colSpan={8}
																className="!max-w-none !p-0"
															>
																<div
																	// -mx-4 cancels the DS Table's !px-4, so the panel spans the full width.
																	className="-mx-4 grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none"
																	style={{
																		gridTemplateRows: expanded ? '1fr' : '0fr'
																	}}
																>
																	{/* The clipping wrapper is ALSO the sticky element — an overflow:hidden
															    ancestor would trap stickiness. */}
																	<div
																		className="overflow-hidden sticky left-0 rounded-b-lg bg-[rgb(var(--cg-canvas))]"
																		style={{ width: viewportW }}
																	>
																		<RuleDetailsDrawer
																			rule={rule}
																			days={days}
																			onDuplicate={() => duplicateRule(rule)}
																			onDelete={() => setDeletingRule(rule)}
																			onSaveAsTemplate={() =>
																				setSavingRule(rule)
																			}
																		/>
																	</div>
																</div>
															</TableCell>
														</TableRow>
													</Fragment>
												)
											})}
										</TableBody>
									</Table>
								</FadingScrollArea>
								<Pagination
									page={currentPage}
									totalPages={totalPages}
									onPageChange={setPage}
									pageSize={pageSize}
									pageSizeOptions={[5, 10, 25]}
									onPageSizeChange={(size) => {
										setPageSize(size)
										setPage(1)
									}}
								/>
							</>
						)}
					</>
				)}
			</div>

			<RuleSheet
				open={sheetOpen}
				rule={editingRule}
				initialDraft={sheetDraft}
				templateName={sheetTemplateName}
				template={editingTemplate}
				onClose={() => setSheetOpen(false)}
				onSave={saveRule}
				onSaveTemplate={saveTemplate}
			/>

			<SaveAsTemplateDialog
				rule={savingRule}
				onClose={() => setSavingRule(null)}
				onSave={(name, scope) => {
					if (savingRule) saveAsTemplate(savingRule, name, scope)
				}}
			/>

			<ConfirmDialog
				open={deletingRule !== null}
				onClose={() => setDeletingRule(null)}
				onConfirm={() => {
					if (deletingRule) deleteRule(deletingRule)
				}}
				tone="danger"
				title="Delete this rule?"
				description={
					deletingRule
						? `"${deletingRule.name}" stops matching traffic immediately. This can't be undone.`
						: undefined
				}
				confirmLabel="Delete rule"
			/>
		</>
	)
}

function toggleIn<T>(list: T[], value: T): T[] {
	return list.includes(value)
		? list.filter((x) => x !== value)
		: [...list, value]
}
